import { BookOpen, Calendar, CheckCircle2, Clock3, Globe2, MapPin, Plus, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/common/Button';
import { DataTable } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/common/StatusBadge';
import { createClass, getClasses, getCourses, makeClassStudentVisible } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatClassSchedule, buildClassAccessPath } from '../../utils/classSchedule';

const DEFAULT_VALUES = {
  title: '',
  courseId: '',
  startTime: '08:00',
  endTime: '10:00',
  instructorId: '',
  instructorName: '',
  locationType: 'online',
  meetingUrl: '',
  physicalLocation: '',
  room: '',
  capacity: '',
  contentType: 'course materials',
  releaseDate: '',
  dueDate: '',
  publicAccessEnabled: true,
};

const CLASS_MODES = {
  online: { label: 'Virtual', description: 'Live online teaching', icon: Globe2, tone: 'blue' },
  physical: { label: 'Physical', description: 'In-person classroom', icon: MapPin, tone: 'green' },
  hybrid: { label: 'Hybrid', description: 'In-person + virtual', icon: UsersRound, tone: 'purple' },
  self_paced: { label: 'Self-paced', description: 'On-demand learning', icon: BookOpen, tone: 'warm' },
};

function getClassModeFromCourse(course) {
  switch (course?.courseType) {
    case 'in_person': return 'physical';
    case 'hybrid': return 'hybrid';
    case 'self_paced': return 'self_paced';
    case 'live_online':
    default: return 'online';
  }
}

export function ClassesPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId } = useTenant();
  const { notify } = useToast();
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [modeFilter, setModeFilter] = useState('all');
  const [values, setValues] = useState(DEFAULT_VALUES);

  const load = useCallback(async () => {
    setLoading(true);
    const [loadedClasses, nextCourses] = await Promise.all([getClasses(activeTenantId), getCourses(activeTenantId)]);
    // Older classes were created before student class access was stored.
    // Backfill them when an authorised administrator opens this page so
    // enrolled students can read the same classes shown here.
    const legacyClasses = loadedClasses.filter((item) => item.publicAccess?.enabled !== true && item.status !== 'deleted');
    if (legacyClasses.length) {
      await Promise.all(legacyClasses.map((item) => makeClassStudentVisible(activeTenantId, item.id)));
    }
    const nextClasses = loadedClasses.map((item) => legacyClasses.some((legacy) => legacy.id === item.id)
      ? { ...item, publicAccess: { enabled: true, label: 'Student login' } }
      : item);
    setClasses(nextClasses);
    setCourses(nextCourses);
    setValues((current) => {
      const courseId = current.courseId || nextCourses[0]?.id || '';
      const selectedCourse = nextCourses.find((course) => course.id === courseId);
      return { ...current, courseId, locationType: getClassModeFromCourse(selectedCourse) };
    });
    setLoading(false);
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  function resetForm(nextCourses = courses) {
    setValues({
      ...DEFAULT_VALUES,
      instructorId: activeTenant?.type === 'private_instructor' ? user.uid : '',
      instructorName: activeTenant?.type === 'private_instructor' ? (user.displayName || user.email || '') : '',
      courseId: nextCourses[0]?.id || '',
      locationType: getClassModeFromCourse(nextCourses[0]),
    });
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (!courses.length) return;
    setSaving(true);
    try {
      const isPrivateInstructor = activeTenant?.type === 'private_instructor';
      await createClass(activeTenantId, user.uid, {
        ...values,
        instructorId: isPrivateInstructor ? user.uid : values.instructorId,
        instructorName: isPrivateInstructor ? (user.displayName || user.email || '') : values.instructorName,
      });
      notify('Continuous class created successfully.');
      resetForm(courses);
      setOpen(false);
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to create class. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const accessPath = (classId) => buildClassAccessPath(activeTenant?.slug || '', classId);
  const onlineClasses = classes.filter((item) => item.locationType === 'online').length;
  const physicalClasses = classes.filter((item) => item.locationType === 'physical').length;
  const hybridClasses = classes.filter((item) => item.locationType === 'hybrid').length;
  const selfPacedClasses = classes.filter((item) => item.locationType === 'self_paced').length;
  const activeClasses = classes.filter((item) => item.status !== 'inactive' && item.status !== 'archived').length;
  const visibleClasses = modeFilter === 'all' ? classes : classes.filter((item) => (item.locationType || 'online') === modeFilter);

  return (
    <div className="page-stack operations-page classes-page">
      <section className="classes-intro">
        <div className="classes-intro-copy">
          <span className="classes-kicker">Every way your learners learn</span>
          <h1>Put every class<br /><i>in motion.</i></h1>
          <p>Plan virtual, physical, hybrid and self-paced learning from one calm schedule. Each class keeps the right place, access details and learner experience together.</p>
        </div>
        <div className="classes-intro-actions">
          <Button icon={Plus} onClick={() => setOpen(true)}>New continuous class</Button>
        </div>
      </section>

      <section className="classes-snapshot" aria-label="Class schedule overview">
        <article className="classes-snapshot-card classes-snapshot-card--primary">
          <span className="classes-snapshot-icon"><Calendar size={16} /></span>
          <div><small>Total classes</small><strong>{classes.length}</strong><p>Your complete teaching schedule.</p></div>
        </article>
        <article className="classes-snapshot-card classes-snapshot-card--green">
          <span className="classes-snapshot-icon"><CheckCircle2 size={16} /></span>
          <div><small>Running now</small><strong>{activeClasses}</strong><p>Ready for students to join.</p></div>
        </article>
        <article className="classes-snapshot-card classes-snapshot-card--blue">
          <span className="classes-snapshot-icon"><Globe2 size={16} /></span>
          <div><small>Virtual rooms</small><strong>{onlineClasses}</strong><p>Online classes with access links.</p></div>
        </article>
        <article className="classes-snapshot-card classes-snapshot-card--warm">
          <span className="classes-snapshot-icon"><MapPin size={16} /></span>
          <div><small>Physical rooms</small><strong>{physicalClasses}</strong><p>In-person classroom sessions.</p></div>
        </article>
        <article className="classes-snapshot-card classes-snapshot-card--purple">
          <span className="classes-snapshot-icon"><UsersRound size={16} /></span>
          <div><small>Hybrid classes</small><strong>{hybridClasses}</strong><p>Learning from room or home.</p></div>
        </article>
        <article className="classes-snapshot-card classes-snapshot-card--self-paced">
          <span className="classes-snapshot-icon"><BookOpen size={16} /></span>
          <div><small>Self-paced</small><strong>{selfPacedClasses}</strong><p>On-demand learning journeys.</p></div>
        </article>
      </section>
      {!courses.length && !loading ? (
        <EmptyState title="Create a course before scheduling a class." message="Classes must belong to a tenant course." actionLabel="Create Course" to="/dashboard/courses" icon={Calendar} />
      ) : loading ? <LoadingState label="Loading classes..." /> : (
        <section className="classes-schedule">
          <div className="classes-schedule-heading">
            <div><span className="classes-section-kicker">Your learning rhythm</span><h2>Classes and learning spaces</h2><p>Every delivery mode has a clear home for your team and learners.</p></div>
            <div className="classes-schedule-controls"><select aria-label="Filter classes by mode" value={modeFilter} onChange={(event) => setModeFilter(event.target.value)}><option value="all">All modes</option>{Object.entries(CLASS_MODES).map(([value, mode]) => <option key={value} value={value}>{mode.label}</option>)}</select><span className="classes-schedule-meta"><Clock3 size={14} /> {visibleClasses.length} class{visibleClasses.length === 1 ? '' : 'es'}</span></div>
          </div>
          <DataTable
            rows={visibleClasses}
            columns={[
            {
              key: 'title',
              label: 'Class',
              render: (item) => (
                <div className="table-cell-stack">
                  <strong>{item.title}</strong>
                  <small>{CLASS_MODES[item.locationType || 'online']?.label || 'Virtual'} learning</small>
                </div>
              ),
            },
            { key: 'courseId', label: 'Course', render: (item) => courses.find((course) => course.id === item.courseId)?.name || '-' },
            { key: 'schedule', label: 'Schedule / access', render: (item) => item.locationType === 'self_paced' ? `${item.selfPaced?.contentType || 'Course materials'} · On demand` : formatClassSchedule(item) },
            { key: 'mode', label: 'Mode', render: (item) => { const mode = CLASS_MODES[item.locationType || 'online'] || CLASS_MODES.online; const Icon = mode.icon; return <span className={`class-mode-badge class-mode-badge--${mode.tone}`}><Icon size={13} />{mode.label}</span>; } },
            { key: 'instructorName', label: 'Instructor', render: (item) => item.instructorName || 'Not assigned' },
            {
              key: 'access',
              label: 'Join class',
              render: (item) => (
                <div className="table-actions">
                  <Button to={accessPath(item.id)} variant="secondary">Join class</Button>
                </div>
              ),
            },
            { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
          ]}
            empty={<EmptyState title="No classes yet." message="Create your first continuous class and share the login link with students." actionLabel="New Continuous Class" onAction={() => setOpen(true)} icon={Calendar} />}
          />
        </section>
      )}
      <Modal className="operation-modal" open={open} onClose={() => setOpen(false)} title="Add a learning space" description="Choose how learners will experience this class, then add the details your team needs.">
        <form onSubmit={onSubmit} className="modal-form">
          {activeTenant?.type === 'private_instructor' ? (
            <div className="notice-box">You are the instructor for this private academy class. Your name will be saved automatically.</div>
          ) : null}
          <FormField label="Title" id="class-title">
            <input id="class-title" required value={values.title} onChange={(event) => setValues({ ...values, title: event.target.value })} placeholder="Morning Web Development Class" />
          </FormField>
          <FormField label="Course" id="class-course">
              <select id="class-course" required value={values.courseId} onChange={(event) => { const courseId = event.target.value; const selectedCourse = courses.find((course) => course.id === courseId); setValues({ ...values, courseId, locationType: getClassModeFromCourse(selectedCourse) }); }}>
              {courses.map((course) => <option key={course.id} value={course.id}>{course.name}</option>)}
            </select>
          </FormField>
          <FormField label="How will learners attend?" id="location-type">
            <select id="location-type" disabled value={values.locationType}>
              {Object.entries(CLASS_MODES).map(([value, mode]) => <option key={value} value={value}>{mode.label} — {mode.description}</option>)}
            </select>
          </FormField>
          <div className="notice-box">This is set automatically from the selected course: <strong>{CLASS_MODES[values.locationType]?.label}</strong>. {values.locationType === 'self_paced' ? 'Learners can progress through materials when it suits them.' : 'Set the details below so learners know exactly how to join.'}</div>
          {values.locationType !== 'self_paced' ? <div className="form-grid">
            <FormField label="Start Time" id="start-time">
              <input id="start-time" type="time" required value={values.startTime} onChange={(event) => setValues({ ...values, startTime: event.target.value })} />
            </FormField>
            <FormField label="End Time" id="end-time">
              <input id="end-time" type="time" required value={values.endTime} onChange={(event) => setValues({ ...values, endTime: event.target.value })} />
            </FormField>
          </div> : null}
          {activeTenant?.type !== 'private_instructor' ? (
            <FormField label="Instructor" id="class-instructor">
              <input id="class-instructor" value={values.instructorName} onChange={(event) => setValues({ ...values, instructorName: event.target.value })} placeholder="Instructor name" />
            </FormField>
          ) : null}
          {['online', 'hybrid'].includes(values.locationType) ? (
            <FormField label="Meeting URL" id="meeting-url">
              <input id="meeting-url" type="url" value={values.meetingUrl} onChange={(event) => setValues({ ...values, meetingUrl: event.target.value })} placeholder="https://..." />
            </FormField>
          ) : null}
          {['physical', 'hybrid'].includes(values.locationType) ? <div className="form-grid"><FormField label="Location / campus" id="physical-location"><input id="physical-location" value={values.physicalLocation} onChange={(event) => setValues({ ...values, physicalLocation: event.target.value })} placeholder="Main campus" /></FormField><FormField label="Room" id="class-room"><input id="class-room" value={values.room} onChange={(event) => setValues({ ...values, room: event.target.value })} placeholder="Room 204" /></FormField><FormField label="Learner capacity" id="class-capacity"><input id="class-capacity" type="number" min="0" value={values.capacity} onChange={(event) => setValues({ ...values, capacity: event.target.value })} placeholder="Unlimited" /></FormField></div> : null}
          {values.locationType === 'self_paced' ? <div className="form-grid"><FormField label="Content available" id="content-type"><select id="content-type" value={values.contentType} onChange={(event) => setValues({ ...values, contentType: event.target.value })}><option value="course materials">Videos, audio and PDFs</option><option value="lessons">Lessons and activities</option><option value="modules">Structured modules</option></select></FormField><FormField label="Release date" id="release-date"><input id="release-date" type="date" value={values.releaseDate} onChange={(event) => setValues({ ...values, releaseDate: event.target.value })} /></FormField><FormField label="Suggested completion date" id="due-date"><input id="due-date" type="date" value={values.dueDate} onChange={(event) => setValues({ ...values, dueDate: event.target.value })} /></FormField></div> : null}
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving} disabled={!courses.length}>Save Class</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
